// Levenshtein distance for string similarity
export function levenshteinDistance(str1: string, str2: string): number {
  const m = str1.length;
  const n = str2.length;
  const dp: number[][] = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));

  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
      }
    }
  }

  return dp[m][n];
}

// Calculate similarity percentage between two strings
export function calculateSimilarity(str1: string, str2: string): number {
  const s1 = str1.toUpperCase().trim();
  const s2 = str2.toUpperCase().trim();
  
  if (s1 === s2) return 100;
  if (s1.length === 0 || s2.length === 0) return 0;

  const distance = levenshteinDistance(s1, s2);
  const maxLength = Math.max(s1.length, s2.length);
  return Math.round((1 - distance / maxLength) * 100);
}

// Normalize description for comparison
export function normalizeDescription(description: string): string {
  return description
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[^A-Z0-9\s]/g, '') // Remove special chars
    .replace(/\s+/g, ' ')
    .trim();
}

// Extract merchant pattern from description (first 2-3 significant words)
export function extractMerchantPattern(description: string): string {
  const normalized = normalizeDescription(description);
  const words = normalized.split(' ').filter(w => w.length > 2);
  
  // Skip common prefixes
  const skipWords = ['PIX', 'TED', 'DOC', 'PGTO', 'PAGTO', 'COMPRA', 'DEBITO', 'CREDITO'];
  const significantWords = words.filter(w => !skipWords.includes(w));
  
  return significantWords.slice(0, 3).join(' ') || words.slice(0, 2).join(' ');
}

export interface ExistingTransaction {
  id: string;
  description: string;
  amount: number;
  transaction_date: string;
}

export interface DuplicateCheckResult {
  isDuplicate: boolean;
  similarity: number;
  matchedTransaction?: ExistingTransaction;
}

// Check if a transaction is a potential duplicate
export function checkDuplicate(
  newTransaction: { description: string; amount: number; date: string },
  existingTransactions: ExistingTransaction[],
  threshold: number = 80
): DuplicateCheckResult {
  const newNormalized = normalizeDescription(newTransaction.description);
  
  for (const existing of existingTransactions) {
    // Check if same date and amount
    if (existing.transaction_date === newTransaction.date && 
        Math.abs(existing.amount - newTransaction.amount) < 0.01) {
      
      const existingNormalized = normalizeDescription(existing.description);
      const similarity = calculateSimilarity(newNormalized, existingNormalized);
      
      if (similarity >= threshold) {
        return {
          isDuplicate: true,
          similarity,
          matchedTransaction: existing
        };
      }
    }
  }
  
  return { isDuplicate: false, similarity: 0 };
}
